import PropTypes from 'prop-types';
import {useResponsive} from 'packages/common/hooks/useResponsive';
import DashboardLayout from './layout';
import {FullHeightLayoutProvider, useFullHeightBreakpoint} from './full-height-context';

/**
 * @file Layout wrapper qui active le mode pleine hauteur à la demande des pages.
 * @module layouts.app.dynamic-layout
 *
 * Certaines pages (réservations restaurant, éditeur de plan de salle) gèrent
 * leur propre layout interne (plan + liste) et doivent occuper toute la hauteur
 * de l'écran sur desktop. Elles le déclarent elles-mêmes via
 * `useFullHeightLayout(breakpoint)` : ce fichier n'a donc aucune connaissance
 * des routes de l'app, et le seuil de bascule reste défini au même endroit que
 * le layout responsive de la page (source unique, pas de désynchronisation).
 *
 * En dessous du breakpoint déclaré, fullHeight est désactivé : scroll naturel,
 * pull-to-refresh et bottom nav mobile conservent leur comportement standard.
 */

/**
 * Applique fullHeight au DashboardLayout selon le breakpoint réclamé par la page
 * courante et la taille d'écran.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @return {JSX.Element}
 */
function DashboardLayoutWithFullHeight({children}) {
  const breakpoint = useFullHeightBreakpoint();

  // Le hook doit être appelé inconditionnellement avec un breakpoint valide ;
  // son résultat n'est retenu que si une page a effectivement déclaré un besoin.
  const isUp = useResponsive('up', breakpoint || 'lg');
  const needsFullHeight = breakpoint !== null && isUp;

  return <DashboardLayout fullHeight={needsFullHeight}>{children}</DashboardLayout>;
}

DashboardLayoutWithFullHeight.propTypes = {
  children: PropTypes.node,
};

/**
 * Layout dynamique : fournit le contexte de pleine hauteur puis applique le mode
 * au DashboardLayout selon ce que la page affichée déclare.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Contenu de la page
 * @return {JSX.Element}
 */
export default function DynamicDashboardLayout({children}) {
  return (
    <FullHeightLayoutProvider>
      <DashboardLayoutWithFullHeight>{children}</DashboardLayoutWithFullHeight>
    </FullHeightLayoutProvider>
  );
}

DynamicDashboardLayout.propTypes = {
  children: PropTypes.node,
};
